// src/pages/Upload.js
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { apiFetch, setMsalContext, formatBytes } from '../utils/api';

function CopyButton({ value }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button className="btn btn-secondary btn-sm" onClick={copy} title="Copy to clipboard" style={{ whiteSpace: 'nowrap' }}>
      {copied ? '✅ Copied' : '📋 Copy'}
    </button>
  );
}

function TagsInput({ values, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const inputRef = useRef();

  const addTag = (val) => {
    const email = val.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    if (!values.includes(email)) onChange([...values, email]);
    setInput('');
  };

  return (
    <div className="tags-input-wrapper" onClick={() => inputRef.current?.focus()}>
      {values.map(v => (
        <span key={v} className="tag-chip">
          {v}
          <button className="tag-remove" onClick={e => { e.stopPropagation(); onChange(values.filter(x => x !== v)); }}>×</button>
        </span>
      ))}
      <input ref={inputRef} className="tags-input" value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); addTag(input); } else if (e.key === 'Backspace' && !input) onChange(values.slice(0, -1)); }}
        onBlur={() => addTag(input)}
        placeholder={values.length === 0 ? placeholder : ''} />
    </div>
  );
}

export default function Upload() {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const fileInputRef = useRef();
  useEffect(() => setMsalContext(instance, accounts[0]), [instance, accounts]);

  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [message, setMessage] = useState('');
  const [expiryDays, setExpiryDays] = useState(2);
  const [viewOnly, setViewOnly] = useState(false);
  const [dragover, setDragover] = useState(false);
  const [step, setStep] = useState('form');
  const [progress, setProgress] = useState({});
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [usePassword, setUsePassword] = useState(false);

  const handlePasswordToggle = (checked) => setUsePassword(checked);

  const onDrop = useCallback(e => {
    e.preventDefault(); setDragover(false);
    setFiles(f => [...f, ...Array.from(e.dataTransfer.files)]);
  }, []);

  const viewOnlyEligible = files.every(f => f.name.toLowerCase().endsWith('.pdf'));

  const handleSubmit = async () => {
    if (!files.length) return setError('Please add at least one file');
    if (!recipients.length) return setError('Please add at least one recipient email');
    if (!title.trim()) return setError('Please enter a title for this transfer');
    setError(''); setStep('uploading');

    try {
      // Phase 1: get upload URLs — no record created yet
      const prepared = await apiFetch('transfers', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          fileNames: files.map(f => f.name),
          fileSizes: files.map(f => f.size),
          expiryDays, recipients, message: message.trim(),
          viewOnly: viewOnly && viewOnlyEligible,
          usePassword,
        }),
      });

      // Phase 2: upload files directly to blob storage
      for (const file of files) {
        setProgress(p => ({ ...p, [file.name]: 0 }));
        const sasUrl = prepared.uploadUrls[file.name];
        if (!sasUrl) throw new Error(`No upload URL for ${file.name}`);
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('PUT', sasUrl);
          xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
          xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          xhr.upload.addEventListener('progress', e => {
            if (e.lengthComputable) setProgress(p => ({ ...p, [file.name]: Math.round((e.loaded / e.total) * 100) }));
          });
          xhr.onload = () => xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status}) — check CORS settings on the storage account`));
          xhr.onerror = () => reject(new Error('Network error uploading file — check CORS settings on the storage account'));
          xhr.send(file);
        });
      }

      // Phase 3: confirm — API verifies blobs exist, then creates records + sends emails
      const confirmed = await apiFetch('transfers/confirm', {
        method: 'POST',
        body: JSON.stringify({
          blobPrefix: prepared.blobPrefix,
          title: title.trim(),
          fileNames: files.map(f => f.name),
          fileSizes: files.map(f => f.size),
          expiryDays, recipients, message: message.trim(),
          viewOnly: viewOnly && viewOnlyEligible,
          usePassword,
        }),
      });

      setResult(confirmed);
      setStep('done');
    } catch (e) {
      setError(e.message);
      setStep('form');
    }
  };

  if (step === 'done' && result) {
    const expiry = new Date(result.expiresAt).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return (
      <div>
        <div className="page-eyebrow">File Transfers</div>
        <h1 className="page-title">Transfer Sent</h1>
        <p className="page-subtitle">Your files are uploaded</p>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '56px', marginBottom: '14px' }}>✅</div>
            <h2 style={{ color: 'var(--g900)', fontSize: '18px', fontWeight: 800, marginBottom: '8px' }}>Transfer created</h2>
            <p style={{ color: 'var(--g500)', fontSize: '14px', marginBottom: '24px' }}>
              {recipients.length - (result.emailFailures || []).length} of {recipients.length} recipient{recipients.length !== 1 ? 's' : ''} emailed. Link expires <strong>{expiry}</strong>.
            </p>
            {(result.emailFailures || []).length > 0 && (
              <div className="alert alert-error" style={{ textAlign: 'left', marginBottom: '20px' }}>
                <strong>⚠️ Notification could not be emailed to:</strong>
                {(result.emailFailures || []).map(f => <div key={f.email}>{f.email}</div>)}
                <div style={{ marginTop: '6px', fontSize: '12px' }}>The transfer was created, but these recipients have not been notified. Please contact IT Support (it.support@sufc.co.uk).</div>
              </div>
            )}
            <div style={{ background: 'var(--g50)', borderRadius: 'var(--radius)', padding: '14px', marginBottom: '24px', textAlign: 'left', display: 'inline-block', minWidth: '260px' }}>
              <div style={{ fontSize: '11px', color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Notified recipients</div>
              {recipients.map(r => <div key={r} style={{ fontSize: '13px', color: 'var(--g700)', padding: '3px 0' }}>📧 {r}</div>)}
              {result.password && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: 'var(--amber-light)', border: '1px solid var(--amber)' }}>
                  <div style={{ fontSize: '11px', color: 'var(--amber)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>🔑 Transfer Password</div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                    <code style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '0.14em', color: 'var(--g900)', fontFamily: 'var(--font-mono)', flex: 1 }}>{result.password}</code>
                    <CopyButton value={result.password} />
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--g600)' }}>Share this password with recipients separately — not by email</div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={() => navigate('/')}>View my transfers</button>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>Send another</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'uploading') {
    const done = files.filter(f => (progress[f.name] || 0) >= 100).length;
    return (
      <div>
        <h1 className="page-title">Uploading…</h1>
        <p className="page-subtitle">Please keep this page open</p>
        <div className="card">
          <div className="card-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '13px', color: 'var(--g600)' }}>Uploading {files.length} file{files.length !== 1 ? 's' : ''}…</span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--g800)' }}>{done}/{files.length}</span>
            </div>
            <div className="progress-bar-wrap"><div className="progress-bar" style={{ width: `${files.length ? (done / files.length) * 100 : 0}%` }} /></div>
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {files.map(f => (
                <div key={f.name}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', color: 'var(--g700)' }}>{f.name}</span>
                    <span style={{ fontSize: '12px', color: 'var(--g400)' }}>{progress[f.name] || 0}%</span>
                  </div>
                  <div className="progress-bar-wrap"><div className="progress-bar" style={{ width: `${progress[f.name] || 0}%` }} /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-eyebrow">File Transfers</div>
      <h1 className="page-title">New File Transfer</h1>
      <p className="page-subtitle">Upload files and share them securely with external recipients</p>

      {error && <div className="alert alert-error">⚠️ {error}</div>}

      <div className="transfer-form-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div className="card">
            <div className="card-header"><span className="card-title">Transfer details</span></div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Transfer title</label>
                <input className="form-input" placeholder="e.g. Player Medical Records — June 2026" value={title} onChange={e => setTitle(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Recipients</label>
                <TagsInput values={recipients} onChange={setRecipients} placeholder="Type email and press Enter…" />
                <span className="form-hint">Each recipient gets a unique link and must verify via email OTP</span>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Message to recipients <span style={{ color: 'var(--g400)', fontWeight: 400 }}>(optional)</span></label>
                <textarea className="form-textarea" rows={3} maxLength={500} value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="e.g. Hi Sam — here are the documents we discussed on Tuesday." style={{ resize: 'vertical', width: '100%', boxSizing: 'border-box' }} />
                <span className="form-hint">Shown in the email, so recipients know it's genuinely from you. Please don't include links or passwords. {message.length}/500</span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Files</span></div>
            <div className="card-body">
              {files.length > 0 && (
                <div className="file-list-items" style={{ marginBottom: '12px' }}>
                  {files.map((f, i) => (
                    <div key={i} className="file-item">
                      <span style={{ fontSize: '16px' }}>📄</span>
                      <span className="file-item-name">{f.name}</span>
                      <span className="file-item-size">{formatBytes(f.size)}</span>
                      <button className="file-remove" onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))}>×</button>
                    </div>
                  ))}
                </div>
              )}
              <div className={`dropzone${dragover ? ' dragover' : ''}`}
                onDragOver={e => { e.preventDefault(); setDragover(true); }}
                onDragLeave={() => setDragover(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}>
                <div className="dropzone-icon">📁</div>
                <div className="dropzone-title">{files.length > 0 ? 'Add more files' : 'Drag files here, or click to browse'}</div>
                <div className="dropzone-subtitle">Any file type · Max 2 GB total</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={e => {
                  const selected = Array.from(e.target.files || []);
                  if (selected.length) setFiles(f => [...f, ...selected]);
                  e.target.value = null;
                }}
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div className="card" style={{ borderLeft: '3px solid var(--red)' }}>
            <div className="card-body" style={{ padding: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--red)', marginBottom: '10px' }}>🔒 Security</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  ['🔐', 'Files are stored in encrypted Azure Blob Storage (AES-256)'],
                  ['📧', 'Recipients verify identity via one-time email code before access'],
                  ['⏱', 'Links auto-expire — you control when'],
                  ['🗑', 'Files are permanently deleted after expiry or download'],
                  ['📋', 'Every access attempt is logged in a full audit trail'],
                  ['🔑', 'Optional password adds a second layer of protection'],
                ].map(([icon, text]) => (
                  <div key={text} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: '14px', flexShrink: 0 }}>{icon}</span>
                    <span style={{ fontSize: '12px', color: 'var(--g600)', lineHeight: '1.4' }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Expiry</span></div>
            <div className="card-body">
              <p style={{ fontSize: '12px', color: 'var(--g500)', marginBottom: '10px' }}>How long should this link be active?</p>
              <div className="expiry-options">
                {[1,2,3,4,5].map(d => (
                  <button key={d} className={`expiry-option${expiryDays === d ? ' selected' : ''}`} onClick={() => setExpiryDays(d)}>
                    <span className="days">{d}</span>
                    <span className="label">{d === 1 ? 'day' : 'days'}</span>
                  </button>
                ))}
              </div>
              <p style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '10px' }}>Auto-expires on first download regardless of date</p>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><span className="card-title">Optional Features</span></div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: viewOnlyEligible ? 'pointer' : 'not-allowed', opacity: viewOnlyEligible ? 1 : 0.5 }}>
                <input type="checkbox" checked={viewOnly && viewOnlyEligible} onChange={e => setViewOnly(e.target.checked)} disabled={!viewOnlyEligible} style={{ marginTop: '3px', accentColor: 'var(--red)', width: '15px', height: '15px' }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--g800)' }}>👁 View-only mode</div>
                  <div style={{ fontSize: '12px', color: 'var(--g500)', marginTop: '2px' }}>PDF only — recipients can view in browser but cannot download{!viewOnlyEligible && files.length > 0 ? ' (PDF files only)' : ''}</div>
                </div>
              </label>
              <div style={{ borderTop: '1px solid var(--g100)', paddingTop: '14px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={usePassword} onChange={e => handlePasswordToggle(e.target.checked)} style={{ marginTop: '3px', accentColor: 'var(--red)', width: '15px', height: '15px' }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--g800)' }}>🔑 Password protection</div>
                    <div style={{ fontSize: '12px', color: 'var(--g500)', marginTop: '2px' }}>Recipients must enter a password in addition to the email OTP</div>
                  </div>
                </label>
                {usePassword && (
                  <div style={{ marginTop: '10px', padding: '12px', background: 'var(--g50)', border: '1px solid var(--g200)' }}>
                    <div style={{ fontSize: '12px', color: 'var(--g700)', lineHeight: 1.5 }}>
                      A secure password will be <strong>generated for you when you send</strong>. It's shown once on the confirmation screen — share it with the recipient separately, not by email.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <button className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }}
            onClick={handleSubmit} disabled={!files.length || !recipients.length || !title.trim()}>
            🚀 Send Transfer
          </button>
        </div>
      </div>
    </div>
  );
}
